import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Autocomplete from '../common/Autocomplete';
import { ExpenseCreate } from '../../types/expense';
import { Restaurant } from '../../types/api';

interface ExpenseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ExpenseCreate) => void;
  restaurants: Restaurant[];
  articles: string[];
  initialData?: Partial<ExpenseCreate & { user_id?: string }>;
  selectedRestaurantId?: string; // Добавляем выбранный ресторан
  isEditing?: boolean; // Добавляем флаг редактирования
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  restaurants: _restaurants, // Переименовываем чтобы избежать предупреждения
  articles,
  initialData,
  selectedRestaurantId,
  isEditing = false
}) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<ExpenseCreate>({
    defaultValues: {
      ...initialData,
      amount: initialData?.amount
    }
  });

  const [articleValue, setArticleValue] = useState(initialData?.article || '');
  
  // Обновляем форму при изменении initialData
  useEffect(() => {
    if (initialData) {
      reset({
        article: initialData.article || '',
        amount: initialData.amount,
        comment: initialData.comment || '',
        restaurant_id: initialData.restaurant_id || '',
        user_id: initialData.user_id || 'user_001'
      });
      
      // Обновляем состояние для автокомплита
      setArticleValue(initialData.article || '');
    } else {
      // Если нет initialData, сбрасываем форму в исходное состояние
      reset({
        article: '',
        amount: undefined,
        comment: '',
        restaurant_id: '',
        user_id: 'user_001'
      });
      setArticleValue('');
    }
  }, [initialData, reset, isEditing]);

  const handleFormSubmit = (data: ExpenseCreate) => {
    // Автоматически добавляем выбранный ресторан
    const dataWithRestaurant = {
      ...data,
      restaurant_id: selectedRestaurantId || data.restaurant_id
    };
    onSubmit(dataWithRestaurant);
    reset();
    setArticleValue('');
    onClose();
  };

  const handleClose = () => {
    reset();
    setArticleValue('');
    onClose();
  };

  const handleArticleChange = (value: string) => {
    setArticleValue(value);
    setValue('article', value, { shouldValidate: true });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditing ? "Редактировать расход" : "Добавить расход"}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div>
          <input type="hidden" {...register('article', { required: 'Выберите статью расхода' })} />
          <Autocomplete
            label="Статья расхода"
            value={articleValue}
            onChange={handleArticleChange}
            options={articles || []}
            placeholder="Начните вводить название статьи..."
            error={errors.article?.message}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Сумма <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            placeholder="Введите сумму"
            {...register('amount', { 
              required: 'Введите сумму',
              min: { value: 0.01, message: 'Сумма должна быть больше 0' }
            })}
            onKeyDown={(e) => {
              // Если пользователь вводит цифру после "0" (но не "0."), заменяем значение
              const input = e.target as HTMLInputElement;
              if (input.value === '0' && /^\d$/.test(e.key)) {
                e.preventDefault();
                input.value = e.key;
                setValue('amount', parseFloat(e.key), { shouldValidate: true });
              }
            }}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.amount ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.amount && (
            <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Комментарий</label>
          <textarea
            {...register('comment')}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
            placeholder="Дополнительная информация..."
          />
        </div>

        <div className="flex space-x-3 pt-4">
           <Button type="submit" className="flex-1">
             {isEditing ? "Сохранить" : "Добавить"}
           </Button>
          <Button type="button" variant="secondary" onClick={handleClose} className="flex-1">
            Отмена
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExpenseForm;
